import type { DepositAccount } from '@fruitful/types';
import type React from 'react';
import { useState } from 'react';
import { Image, Pressable } from 'react-native';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    ChevronRightIcon,
    FootnoteRegular,
    FruitfulCustomIcon,
    FullScreenSpinner,
    Icon,
    isNonMobileWeb,
    Notice,
    ScreenContainer,
    Shadows,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { centsToFormattedDollars } from '../../../common/utils/bankingUtils';
import type { CheckImage } from '../../types/CheckDeposit';
import { TransferAccountIcon } from '../transfers/TransferIcons';
import { CheckDepositLimitsButton } from './CheckDepositLimitsSheet';

interface CheckDepositReviewProps {
    amountCents: number;
    depositAccount: NonNullable<DepositAccount>;
    frontImage: CheckImage;
    backImage: CheckImage;
    isSubmitting: boolean;
    errorMessage: string | null;
    onBack: () => void;
    onRetakeFront: () => void;
    onRetakeBack: () => void;
    onConfirm: () => void;
}

interface CheckImageCardProps {
    label: string;
    image: CheckImage;
    onPress: () => void;
}

const CARD_ASPECT_RATIO = 2.3;

const CheckImageCard = ({ label, image, onPress }: CheckImageCardProps) => {
    const [containerWidth, setContainerWidth] = useState(0);
    const containerHeight = containerWidth / CARD_ASPECT_RATIO;

    return (
        <YStack gap={8}>
            <BodyMediumEmphasized>{label}</BodyMediumEmphasized>
            <Pressable onPress={onPress}>
                <View
                    borderRadius={12}
                    overflow="hidden"
                    borderWidth={1}
                    backgroundColor="$gray100"
                    style={{
                        width: '100%',
                        aspectRatio: CARD_ASPECT_RATIO,
                    }}
                    onLayout={(e) => {
                        setContainerWidth(e.nativeEvent.layout.width);
                    }}
                >
                    {containerWidth > 0 && (
                        <Image
                            source={{ uri: image.uri }}
                            style={{
                                position: 'absolute',
                                transform: [{ rotate: '-90deg' }],
                                width: containerHeight,
                                height: containerWidth,
                                left: (containerWidth - containerHeight) / 2,
                                top: (containerHeight - containerWidth) / 2,
                            }}
                            resizeMode="cover"
                        />
                    )}
                </View>
            </Pressable>
        </YStack>
    );
};

interface SummaryRowProps {
    label: string;
    value: string | React.ReactNode;
}

const SummaryRow = ({ label, value }: SummaryRowProps) => (
    <XStack
        paddingVertical={8}
        borderBottomWidth={1}
        borderBottomColor="$gray100"
        justifyContent="space-between"
        alignItems="center"
    >
        <BodyMediumRegular>{label}</BodyMediumRegular>
        {typeof value === 'string' ? (
            <BodyMediumEmphasized>{value}</BodyMediumEmphasized>
        ) : (
            value
        )}
    </XStack>
);

const CheckDepositReview = ({
    amountCents,
    depositAccount,
    frontImage,
    backImage,
    isSubmitting,
    errorMessage,
    onBack,
    onRetakeFront,
    onRetakeBack,
    onConfirm,
}: CheckDepositReviewProps) => (
    <ScreenContainer title="Review your check" hasBackButton onBack={onBack}>
        <YStack
            height="100%"
            maxWidth={500}
            $sm={{ marginTop: 50 }}
            alignSelf="center"
            width="100%"
        >
            {errorMessage != null && (
                <Notice
                    variant="danger"
                    title="Check deposit failed"
                    description={errorMessage}
                />
            )}
            <XStack
                alignItems="center"
                justifyContent="center"
                marginVertical={24}
            >
                <FruitfulCustomIcon
                    icon="camera"
                    color="greenleaf"
                    size="lg"
                    showShadow
                />
                <View
                    marginLeft={-12}
                    marginRight={-12}
                    alignSelf="flex-start"
                    padding={6}
                    borderRadius={100}
                    borderWidth={1}
                    borderColor="$gray100"
                    backgroundColor="$white"
                    marginTop={18}
                    style={{ ...Shadows.SMALL }}
                    zIndex={1}
                >
                    <Icon icon={ChevronRightIcon} size={16} />
                </View>
                <TransferAccountIcon
                    size="lg"
                    account={depositAccount}
                    showShadow
                />
            </XStack>

            <XStack gap={12} marginBottom={20}>
                <View flex={1}>
                    <CheckImageCard
                        label="Front"
                        image={frontImage}
                        onPress={onRetakeFront}
                    />
                </View>
                <View flex={1}>
                    <CheckImageCard
                        label="Back"
                        image={backImage}
                        onPress={onRetakeBack}
                    />
                </View>
            </XStack>

            <YStack width="100%" marginBottom={20}>
                <SummaryRow
                    label="Amount"
                    value={centsToFormattedDollars(amountCents)}
                />
                <SummaryRow
                    label="Deposit to"
                    value={
                        <YStack alignItems="flex-end">
                            <BodyMediumEmphasized>
                                {depositAccount.nickname ??
                                    depositAccount.bankName}
                            </BodyMediumEmphasized>
                            <FootnoteRegular color="$gray1000">
                                {depositAccount.bankName} *
                                {depositAccount.accountNumberMasked}
                            </FootnoteRegular>
                        </YStack>
                    }
                />
            </YStack>

            <YStack
                marginTop={isNonMobileWeb ? 'none' : 'auto'}
                gap={8}
                width="100%"
            >
                <Button
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.LARGE}
                    text="Submit deposit"
                    onPress={onConfirm}
                    disabled={isSubmitting}
                />
                <CheckDepositLimitsButton />
            </YStack>
        </YStack>
        {isSubmitting && <FullScreenSpinner />}
    </ScreenContainer>
);

export default CheckDepositReview;
